import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useCRM } from '../../context/CRMContext';

const { 
  FiHome, FiUsers, FiFileText, FiGitPullRequest, 
  FiRefreshCw, FiSettings, FiX, FiActivity, 
  FiPackage, FiShield, FiLayers, FiCheckSquare, 
  FiBarChart3, FiTarget, FiUserPlus, FiUser, 
  FiMail, FiKey, FiDollarSign, FiMessageCircle 
} = FiIcons;

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { settings } = useCRM();
  const { user, isAdmin } = useAuth();

  const hasPermission = (permission) => {
    if (isAdmin) return true;
    return user?.permissions?.[permission] === true;
  };

  const allNavItems = [
    { name: 'Dashboard', href: '/', icon: FiHome, permission: 'view_dashboard' },
    { name: 'Messages', href: '/messages', icon: FiMessageCircle },
    { name: 'Leads', href: '/leads', icon: FiTarget, permission: 'view_leads' },
    { name: 'Clients', href: '/clients', icon: FiUsers, permission: 'view_clients' },
    { name: 'Contracts', href: '/contracts', icon: FiFileText, permission: 'view_contracts' },
    { name: 'Services', href: '/services', icon: FiPackage, permission: 'view_services' },
    { name: 'Works', href: '/works', icon: FiActivity, permission: 'view_works' },
    { name: 'Change Requests', href: '/change-requests', icon: FiGitPullRequest, permission: 'view_change_requests' },
    { name: 'Renewals', href: '/renewals', icon: FiRefreshCw, permission: 'view_renewals' },
    { name: 'Tasks', href: '/tasks', icon: FiCheckSquare, permission: 'view_tasks' },
    { name: 'Task Reports', href: '/task-reports', icon: FiBarChart3, permission: 'view_task_reports' },
    { name: 'Payroll', href: '/payroll', icon: FiDollarSign, permission: 'view_user_management' },
    { name: 'Notifications', href: '/notifications', icon: FiMail, permission: 'view_dashboard' },
    { name: 'User Management', href: '/users', icon: FiShield, permission: 'view_user_management' },
    { name: 'Role Management', href: '/roles', icon: FiKey, permission: 'view_user_management' },
    { name: 'Departments', href: '/departments', icon: FiLayers, permission: 'view_departments' },
    { name: 'Lead Sources', href: '/lead-sources', icon: FiUserPlus, permission: 'view_lead_sources' },
    { name: 'Settings', href: '/settings', icon: FiSettings, permission: 'view_settings' },
  ];

  const navigation = allNavItems.filter(item => {
    if (item.name === 'Payroll' && !isAdmin) return false;
    if (item.name === 'Role Management' && !isAdmin) return false;
    if (item.permission) {
      return hasPermission(item.permission);
    }
    return true;
  });

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9998] bg-gray-600 bg-opacity-75 lg:hidden"
            onClick={onClose}
          />
          <motion.div
            initial={{ x: -300 }}
            animate={{ x: 0 }}
            exit={{ x: -300 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed inset-y-0 left-0 z-[9998] w-80 bg-white shadow-2xl flex flex-col"
          >
            <div className="flex-shrink-0 flex items-center justify-between h-16 px-6 bg-primary-600 border-b border-primary-700">
              <div className="flex items-center space-x-3">
                {settings.companyLogo ? (
                  <img src={settings.companyLogo} alt={settings.companyName} className="h-8 w-8 rounded object-cover" />
                ) : (
                  <div className="h-8 w-8 bg-white bg-opacity-20 rounded flex items-center justify-center">
                    <span className="text-white font-bold text-sm">
                      {settings.companyName.charAt(0)}
                    </span>
                  </div>
                )}
                <span className="text-white font-semibold text-lg truncate max-w-[160px]">
                  {settings.companyName}
                </span>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 p-2 rounded-full hover:bg-white hover:bg-opacity-10 transition-all"
              >
                <SafeIcon icon={FiX} className="h-6 w-6" />
              </button>
            </div>

            <nav className="flex-1 px-4 py-6 overflow-y-auto">
              <div className="space-y-2">
                {navigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={onClose}
                      className={`group flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 ${
                        isActive 
                          ? 'bg-primary-50 text-primary-700 border-r-4 border-primary-600 shadow-sm' 
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 hover:shadow-sm'
                      }`}
                    >
                      <SafeIcon
                        icon={item.icon}
                        className={`mr-4 h-5 w-5 transition-colors ${
                          isActive ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-600'
                        }`}
                      />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            </nav>

            <div className="flex-shrink-0 border-t border-gray-200 p-4">
              <Link
                to="/profile"
                onClick={onClose}
                className="flex items-center space-x-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="h-10 w-10 bg-primary-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {user?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {user?.full_name}
                  </p>
                  <p className="text-xs text-gray-500 capitalize">
                    {user?.role}
                  </p>
                </div>
              </Link>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default Sidebar;